/**
 * PublicLeaguePage.js - Public league page viewer
 * 
 * Displays schedule and standings for a public league
 * No authentication required
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { getSportConfig } from '../utils/sportConfig';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';
import './PublicLeaguePage.css';

// Build base URL for calendar .ics links (same logic as api.js)
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const SERVER_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

function PublicLeaguePage() {
  const { slug } = useParams();
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('schedule');
  const [selectedSeason, setSelectedSeason] = useState(null); // full season data
  const [loadingSeason, setLoadingSeason] = useState(false);

  // Build calendar URLs (webcal:// for subscription when URL is absolute, fallback to http for local dev)
  const calendarBaseUrl = `${SERVER_BASE_URL}/public/${slug}`;
  const toWebcal = (url) => url.replace(/^https?:\/\//, 'webcal://');
  const leagueCalendarUrl = `${calendarBaseUrl}/calendar.ics`;
  const webcalLeagueUrl = SERVER_BASE_URL ? toWebcal(leagueCalendarUrl) : leagueCalendarUrl;
  const teamCalendarUrl = (teamId) => `${calendarBaseUrl}/teams/${teamId}/calendar.ics`;
  const webcalTeamUrl = (teamId) => SERVER_BASE_URL ? toWebcal(teamCalendarUrl(teamId)) : teamCalendarUrl(teamId);

  useEffect(() => {
    loadLeagueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      const response = await api.getPublicLeague(slug);
      setData(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSeason = async (seasonId) => {
    if (!seasonId) {
      setSelectedSeason(null);
      setActiveTab('schedule');
      return;
    }
    try {
      setLoadingSeason(true);
      const result = await api.getPublicSeason(slug, seasonId);
      setSelectedSeason(result.season);
      setActiveTab('history');
    } catch (err) {
      console.error('Failed to load season:', err);
    } finally {
      setLoadingSeason(false);
    }
  };

  const brandingBar = (
    <div className="public-branding-bar">
      <a href="https://leaguemaker.app" className="public-branding-logo" target="_blank" rel="noopener noreferrer">
        <Logo size="1.2rem" variant="white" />
      </a>
      <a href="https://leaguemaker.app/signup" className="public-branding-cta" target="_blank" rel="noopener noreferrer">
        Start a League — Free
      </a>
    </div>
  );

  if (loading) {
    return (
      <div className="public-page">
        {brandingBar}
        <div className="loading">Loading league...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-page">
        {brandingBar}
        <div className="error-container">
          <h1>⚠️ League Not Found</h1>
          <p>{error}</p>
          <p className="hint">This league may be private or the URL may be incorrect.</p>
        </div>
      </div>
    );
  }

  const { league, standings, teams, schedule, playoffs, seasons } = data;
  const sportConfig = getSportConfig(league.sport);

  // Custom color overrides via CSS custom properties (validated hex format)
  const safeColor = league.primaryColor && /^#[0-9A-Fa-f]{6}$/.test(league.primaryColor) ? league.primaryColor : null;
  const customStyles = safeColor ? {
    '--color-brand': safeColor,
    '--color-brand-gradient': `linear-gradient(135deg, ${safeColor} 0%, ${safeColor}cc 100%)`
  } : {};

  return (
    <div className="public-page" style={customStyles}>
      {brandingBar}
      <header className="public-header">
        <div className="header-content">
          {league.logoUrl && (
            <img
              src={league.logoUrl}
              alt={`${league.name} logo`}
              style={{
                maxHeight: 80,
                maxWidth: 240,
                marginBottom: '0.5rem',
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <h1>{league.name}</h1>
          {league.description && (
            <p style={{
              color: colors.textSecondary,
              fontSize: '1rem',
              margin: '0.25rem 0 0.5rem',
              maxWidth: 600,
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.4
            }}>
              {league.description}
            </p>
          )}
          <p className="season">{league.seasonYear}</p>
          {league.sport && league.sport !== 'general' && (
            <span style={{
              display: 'inline-block',
              background: colors.badgeBg,
              color: colors.badgeText,
              padding: '0.2rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: 500,
              marginTop: '0.25rem'
            }}>
              {sportConfig.label}
            </span>
          )}
        </div>
      </header>

      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => setActiveTab('schedule')}
        >
          Schedule
        </button>
        {league.showStandings !== false && (
          <button
            className={`tab-button ${activeTab === 'standings' ? 'active' : ''}`}
            onClick={() => setActiveTab('standings')}
          >
            Standings
          </button>
        )}
        {playoffs && (
          <button
            className={`tab-button ${activeTab === 'playoffs' ? 'active' : ''}`}
            onClick={() => setActiveTab('playoffs')}
          >
            Playoffs
          </button>
        )}
        <button
          className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendar
        </button>
        {seasons && seasons.length > 0 && (
          <select
            value={activeTab === 'history' && selectedSeason ? selectedSeason.id : ''}
            onChange={(e) => {
              if (e.target.value) {
                handleSelectSeason(e.target.value);
              } else {
                setSelectedSeason(null);
                setActiveTab('schedule');
              }
            }}
            className="tab-button"
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              background: activeTab === 'history'
                ? colors.card
                : colors.bgSecondary,
              color: activeTab === 'history' ? colors.brand : colors.textSecondary,
              fontWeight: activeTab === 'history' ? '600' : '400',
              boxShadow: activeTab === 'history' ? `0 2px 8px ${colors.shadow}` : 'none',
              cursor: 'pointer',
              paddingRight: '2rem',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath d=\'M3 5l3 3 3-3\' fill=\'none\' stroke=\'%23666\' stroke-width=\'1.5\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.6rem center'
            }}
          >
            <option value="">Past Seasons</option>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="content-area">
        {activeTab === 'schedule' && (
          <div className="schedule-view">
            {schedule.map((week) => (
              <div key={week.week} className="week-card">
                <div className="week-header">
                  <h3>Week {week.week}</h3>
                  <span className="week-date">{formatDate(week.date)}</span>
                </div>
                <div className="games-list">
                  {week.games.map((game, index) => (
                    <div key={index} className={`game-row ${game.played ? 'played' : 'upcoming'}`}>
                      <span className="game-time">
                        {game.time}
                        {game.venue && (
                          <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 400, color: colors.textSecondary, marginTop: '2px' }}>
                            {game.venue.name}
                          </span>
                        )}
                      </span>
                      <div className="game-matchup">
                        <span className="team">{game.team1}</span>
                        {game.status === 'forfeit' ? (
                          <span className="score" style={{ color: '#e67e22' }}>
                            FORFEIT{game.forfeitTeam ? ` — ${game.forfeitTeam}` : ''}
                          </span>
                        ) : game.status === 'cancelled' ? (
                          <span className="score" style={{ color: '#dc3545' }}>CANCELLED</span>
                        ) : game.status === 'postponed' ? (
                          <span className="score" style={{ color: '#856404' }}>
                            POSTPONED{game.rescheduledDate ? ` — Rescheduled to ${formatDate(game.rescheduledDate)}` : ''}
                          </span>
                        ) : game.played ? (
                          sportConfig.winLossOnly ? (
                            <span className="score" style={{
                              color: game.team1Score === game.team2Score ? '#6c757d' : '#28a745',
                              fontWeight: 600,
                              fontSize: '0.85rem'
                            }}>
                              {game.team1Score > game.team2Score
                                ? `Win \u2014 ${game.team1}`
                                : game.team2Score > game.team1Score
                                  ? `Win \u2014 ${game.team2}`
                                  : 'Draw'}
                            </span>
                          ) : (
                            <span className="score">{game.team1Score} - {game.team2Score}</span>
                          )
                        ) : (
                          <span className="vs">vs</span>
                        )}
                        <span className="team">{game.team2}</span>
                      </div>
                      {game.status === 'scheduled' && !game.played && <span className="upcoming-badge">Upcoming</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'standings' && league.showStandings !== false && (
          <div className="standings-view">
            <table className="standings-table">
              <thead>
                <tr>
                  <th className="rank-col">#</th>
                  <th className="team-col">Team</th>
                  <th>GP</th>
                  <th>W</th>
                  <th>L</th>
                  {sportConfig.allowTies && <th>T</th>}
                  <th>PTS</th>
                  {!sportConfig.winLossOnly && <th className="hide-mobile">{sportConfig.statLabels.goalsFor}</th>}
                  {!sportConfig.winLossOnly && <th className="hide-mobile">{sportConfig.statLabels.goalsAgainst}</th>}
                  {!sportConfig.winLossOnly && <th className="hide-mobile">{sportConfig.statLabels.goalDifferential}</th>}
                </tr>
              </thead>
              <tbody>
                {standings.map((team, index) => {
                  const teamsQualifying = league.playoffSettings?.teamsQualifying || standings.length;
                  return (
                    <tr key={team.id} className={index < teamsQualifying ? 'playoff-position' : ''}>
                      <td className="rank-col">{index + 1}</td>
                      <td className="team-col">{team.name}</td>
                      <td>{team.gamesPlayed}</td>
                      <td>{team.wins}</td>
                      <td>{team.losses}</td>
                      {sportConfig.allowTies && <td>{team.ties}</td>}
                      <td className="points-col">{team.points}</td>
                      {!sportConfig.winLossOnly && <td className="hide-mobile">{team.goalsFor}</td>}
                      {!sportConfig.winLossOnly && <td className="hide-mobile">{team.goalsAgainst}</td>}
                      {!sportConfig.winLossOnly && <td className="hide-mobile">{team.goalDifferential > 0 ? '+' : ''}{team.goalDifferential}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {standings.some(t => t.gamesPlayed === 0) && (
              <p className="standings-note">
                * Standings will update as games are played
              </p>
            )}
          </div>
        )}

        {activeTab === 'playoffs' && playoffs && (
          <div className="schedule-view">
            {/* Champion banner */}
            {(() => {
              const finalRound = playoffs.rounds[playoffs.rounds.length - 1];
              const finalGame = finalRound?.games[0];
              if (finalGame?.played) {
                const champion = finalGame.overrideWinnerSlot
                  ? (finalGame.overrideWinnerSlot === 1 ? finalGame.team1 : finalGame.team2)
                  : (finalGame.team1Score > finalGame.team2Score ? finalGame.team1 : finalGame.team2);
                return (
                  <div className="week-card" style={{ background: colors.brandGradient, color: 'white', textAlign: 'center' }}>
                    <h3 style={{ color: 'white', margin: '0 0 0.25rem' }}>Champion</h3>
                    <p style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>{champion?.name}</p>
                  </div>
                );
              }
              return null;
            })()}

            {playoffs.rounds.map((round) => (
              <div key={round.round} className="week-card">
                <div className="week-header">
                  <h3>{round.name}</h3>
                  <span className="week-date">{formatDate(round.date)}</span>
                </div>
                <div className="games-list">
                  {round.games.map((game, index) => {
                    const team1Name = game.team1 ? game.team1.name : 'TBD';
                    const team2Name = game.team2 ? game.team2.name : 'TBD';
                    const hasPending = game.team1?.pending || game.team2?.pending;
                    return (
                      <div key={index} className={`game-row ${game.played ? 'played' : 'upcoming'}`}>
                        <span className="game-time">{game.time}</span>
                        <div className="game-matchup">
                          <span className={`team${game.team1?.pending ? ' team-pending' : ''}`}>
                            {game.team1?.seed && <span style={{ color: colors.brand, fontSize: '0.8rem', marginRight: '0.3rem' }}>({game.team1.seed})</span>}
                            {team1Name}
                          </span>
                          {game.played ? (
                            <span className="score">
                              {game.team1Score != null ? `${game.team1Score} - ${game.team2Score}` : 'Override'}
                              {game.overrideWinnerSlot && (
                                <span title={`Admin Override${game.overrideReason ? ': ' + game.overrideReason : ''}`}
                                      style={{ cursor: 'help', marginLeft: '0.25rem', color: '#e9a937' }}>*</span>
                              )}
                            </span>
                          ) : (
                            <span className="vs">vs</span>
                          )}
                          <span className={`team${game.team2?.pending ? ' team-pending' : ''}`}>
                            {team2Name}
                            {game.team2?.seed && <span style={{ color: colors.brand, fontSize: '0.8rem', marginLeft: '0.3rem' }}>({game.team2.seed})</span>}
                          </span>
                        </div>
                        {!game.played && !hasPending && game.team1 && game.team2 && <span className="upcoming-badge">Upcoming</span>}
                        {!game.played && (hasPending || !game.team1 || !game.team2) && <span className="upcoming-badge" style={{ background: colors.bgSecondary, color: colors.textSecondary }}>Pending</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {activeTab === 'calendar' && (
          <div className="public-calendar-view">
            <div className="public-calendar-info-card">
              <h3 className="public-calendar-info-title">Subscribe to Calendar</h3>
              <p className="public-calendar-info-text">
                Click a subscribe button to add games to Google Calendar, Apple Calendar, or Outlook.
                Your calendar will update automatically as scores are entered or changes are made.
              </p>
            </div>

            <div className="public-calendar-section">
              <h3 className="public-calendar-section-title">Full League Calendar</h3>
              <p className="public-calendar-section-desc">All games for every team in one calendar feed.</p>
              <a href={webcalLeagueUrl} className="public-calendar-subscribe-btn public-calendar-subscribe-btn-lg">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Subscribe to Full League Calendar
              </a>
            </div>

            <div className="public-calendar-section">
              <h3 className="public-calendar-section-title">Team Calendars</h3>
              <p className="public-calendar-section-desc">Subscribe to just one team's games.</p>
              <div className="public-calendar-team-list">
                {(teams || []).map((team) => (
                  <div key={team.id} className="public-calendar-team-row">
                    <span className="public-calendar-team-name">{team.name}</span>
                    <a href={webcalTeamUrl(team.id)} className="public-calendar-subscribe-btn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      Subscribe
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {activeTab === 'history' && selectedSeason && (
          <div className="schedule-view">
            {loadingSeason ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: colors.textSecondary }}>Loading season data...</div>
            ) : (
              <>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: colors.brandGradient,
                  borderRadius: 12,
                  color: '#fff'
                }}>
                  <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.3rem', color: '#fff' }}>{selectedSeason.name}</h2>
                  <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.9 }}>
                    Archived {formatDate(selectedSeason.archivedAt)}
                  </p>
                </div>

                {/* Standings */}
                {selectedSeason.standings && selectedSeason.standings.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1rem', color: colors.text, margin: '0 0 0.75rem' }}>Final Standings</h3>
                    <table className="standings-table">
                      <thead>
                        <tr>
                          <th className="rank-col">#</th>
                          <th className="team-col">Team</th>
                          <th>W</th>
                          <th>L</th>
                          <th>T</th>
                          <th>PTS</th>
                          <th className="hide-mobile">GF</th>
                          <th className="hide-mobile">GA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...selectedSeason.standings]
                          .sort((a, b) => {
                            if (b.points !== a.points) return b.points - a.points;
                            const aDiff = a.goalsFor - a.goalsAgainst;
                            const bDiff = b.goalsFor - b.goalsAgainst;
                            if (bDiff !== aDiff) return bDiff - aDiff;
                            return b.goalsFor - a.goalsFor;
                          })
                          .map((team, i) => (
                            <tr key={i}>
                              <td className="rank-col">{i + 1}</td>
                              <td className="team-col">{team.teamName}</td>
                              <td>{team.wins}</td>
                              <td>{team.losses}</td>
                              <td>{team.ties}</td>
                              <td className="points-col">{team.points}</td>
                              <td className="hide-mobile">{team.goalsFor}</td>
                              <td className="hide-mobile">{team.goalsAgainst}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Playoff Results */}
                {selectedSeason.playoffResults && selectedSeason.playoffResults.rounds && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1rem', color: colors.text, margin: '0 0 0.75rem' }}>Playoff Results</h3>
                    {(() => {
                      const finalRound = selectedSeason.playoffResults.rounds[selectedSeason.playoffResults.rounds.length - 1];
                      const finalGame = finalRound?.games[0];
                      if (finalGame?.played) {
                        const champion = finalGame.overrideWinnerSlot
                          ? (finalGame.overrideWinnerSlot === 1 ? finalGame.team1 : finalGame.team2)
                          : (finalGame.team1Score > finalGame.team2Score ? finalGame.team1 : finalGame.team2);
                        return (
                          <div className="week-card" style={{ background: colors.brandGradient, color: 'white', textAlign: 'center' }}>
                            <h3 style={{ color: 'white', margin: '0 0 0.25rem' }}>Champion</h3>
                            <p style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>{champion?.name}</p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {selectedSeason.playoffResults.rounds.map((round) => (
                      <div key={round.round} className="week-card">
                        <div className="week-header">
                          <h3>{round.name}</h3>
                          {round.date && <span className="week-date">{formatDate(round.date)}</span>}
                        </div>
                        <div className="games-list">
                          {round.games.map((game, gi) => (
                            <div key={gi} className={`game-row ${game.played ? 'played' : 'upcoming'}`}>
                              <span className="game-time">{game.time}</span>
                              <div className="game-matchup">
                                <span className="team">
                                  {game.team1?.seed && <span style={{ color: colors.brand, fontSize: '0.8rem', marginRight: '0.3rem' }}>({game.team1.seed})</span>}
                                  {game.team1?.name || 'TBD'}
                                </span>
                                {game.played ? (
                                  <span className="score">
                                    {game.team1Score != null ? `${game.team1Score} - ${game.team2Score}` : 'Override'}
                                    {game.overrideWinnerSlot && (
                                      <span title={`Admin Override${game.overrideReason ? ': ' + game.overrideReason : ''}`}
                                            style={{ cursor: 'help', marginLeft: '0.25rem', color: '#e9a937' }}>*</span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="vs">vs</span>
                                )}
                                <span className="team">
                                  {game.team2?.name || 'TBD'}
                                  {game.team2?.seed && <span style={{ color: colors.brand, fontSize: '0.8rem', marginLeft: '0.3rem' }}>({game.team2.seed})</span>}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Schedule */}
                {selectedSeason.scheduleData && (
                  <div>
                    <h3 style={{ fontSize: '1rem', color: colors.text, margin: '0 0 0.75rem' }}>Season Schedule</h3>
                    {(() => {
                      const sd = selectedSeason.scheduleData;
                      const weeks = sd.schedule || sd.weeks || [];
                      return weeks.map((week, wi) => (
                        <div key={wi} className="week-card">
                          <div className="week-header">
                            <h3>Week {week.week || wi + 1}</h3>
                            {week.date && <span className="week-date">{formatDate(week.date)}</span>}
                          </div>
                          <div className="games-list">
                            {(week.games || []).map((game, gi) => (
                              <div key={gi} className="game-row played">
                                <span className="game-time">{game.time}</span>
                                <div className="game-matchup">
                                  <span className="team">{game.team1 || game.team1Name}</span>
                                  <span className="vs">vs</span>
                                  <span className="team">{game.team2 || game.team2Name}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <footer className="public-footer">
        <p>Powered by LeagueMaker</p>
      </footer>
    </div>
  );
}

export default PublicLeaguePage;
